import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import TeamLogo from './TeamLogo';
import { shortTeamName } from './teamUtils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FormBadge({ result }) {
  const colors = {
    W: 'bg-[#34C759] text-white',
    L: 'bg-[#FF3B30] text-white',
    D: 'bg-gray-400 text-white',
  };
  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-sm text-xs font-bold ${colors[result] || colors.D}`}>
      {result}
    </span>
  );
}

export default function MatchInsights({ sport, homeTeam, awayTeam, fixtureId, roundNumber }) {
  const [expanded, setExpanded] = useState(false);
  const [homeForm, setHomeForm] = useState(null);
  const [awayForm, setAwayForm] = useState(null);
  const [h2h, setH2h] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (expanded && !homeForm) {
      fetchData();
    }
  }, [expanded]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const beforeParam = roundNumber != null ? `?before_round=${roundNumber}` : '';
      const [homeRes, awayRes, h2hRes] = await Promise.all([
        axios.get(`${API_URL}/api/form/${sport}/${encodeURIComponent(homeTeam)}${beforeParam}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/form/${sport}/${encodeURIComponent(awayTeam)}${beforeParam}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/head-to-head/${sport}?home_team=${encodeURIComponent(homeTeam)}&away_team=${encodeURIComponent(awayTeam)}`, { withCredentials: true }),
      ]);
      setHomeForm(homeRes.data);
      setAwayForm(awayRes.data);
      setH2h(h2hRes.data);
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#007AFF] transition-colors mx-auto"
        data-testid={`toggle-insights-${fixtureId}`}
      >
        <TrendingUp className="w-3 h-3" />
        <span>Form & H2H</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-3 bg-gray-50 rounded-sm p-4 space-y-4 animate-fade-up" data-testid={`insights-panel-${fixtureId}`}>
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="w-5 h-5 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Current Form */}
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                  Form Going Into {roundNumber === 0 ? 'Opening Round' : `Round ${roundNumber}`} (Last 5)
                </div>
                <div className="space-y-2">
                  {/* Home Team Form */}
                  <div className="flex items-center gap-3">
                    <TeamLogo teamName={homeTeam} size={24} />
                    <span className="text-sm font-semibold text-[#1A1A1A] w-36 truncate">{homeTeam}</span>
                    <div className="flex gap-1">
                      {homeForm && homeForm.length > 0 ? (
                        homeForm.map((f, i) => <FormBadge key={i} result={f.result} />)
                      ) : (
                        <span className="text-xs text-gray-400">No results yet</span>
                      )}
                    </div>
                    {homeForm && homeForm.length > 0 && (
                      <span className="text-xs text-gray-500 ml-auto">
                        {homeForm.filter(f => f.result === 'W').length}W {homeForm.filter(f => f.result === 'L').length}L
                      </span>
                    )}
                  </div>

                  {/* Away Team Form */}
                  <div className="flex items-center gap-3">
                    <TeamLogo teamName={awayTeam} size={24} />
                    <span className="text-sm font-semibold text-[#1A1A1A] w-36 truncate">{awayTeam}</span>
                    <div className="flex gap-1">
                      {awayForm && awayForm.length > 0 ? (
                        awayForm.map((f, i) => <FormBadge key={i} result={f.result} />)
                      ) : (
                        <span className="text-xs text-gray-400">No results yet</span>
                      )}
                    </div>
                    {awayForm && awayForm.length > 0 && (
                      <span className="text-xs text-gray-500 ml-auto">
                        {awayForm.filter(f => f.result === 'W').length}W {awayForm.filter(f => f.result === 'L').length}L
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Head to Head */}
              {h2h && h2h.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Recent Head-to-Head</div>
                  <div className="space-y-1.5">
                    {h2h.map((match, i) => {
                      const homeWon = match.home_score > match.away_score;
                      const awayWon = match.away_score > match.home_score;
                      return (
                        <div key={i} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded-sm bg-white">
                          <span className="text-xs text-gray-400 w-12 flex-shrink-0">{match.year}</span>
                          <span className={`flex-1 text-right truncate ${homeWon ? 'font-bold text-[#1A1A1A]' : 'text-gray-500'}`}>
                            {shortTeamName(match.home_team)}
                          </span>
                          <span className={`font-bold tabular-nums ${homeWon ? 'text-[#1A1A1A]' : 'text-gray-400'}`}>
                            {match.home_score}
                          </span>
                          <span className="text-gray-300 text-xs">-</span>
                          <span className={`font-bold tabular-nums ${awayWon ? 'text-[#1A1A1A]' : 'text-gray-400'}`}>
                            {match.away_score}
                          </span>
                          <span className={`flex-1 truncate ${awayWon ? 'font-bold text-[#1A1A1A]' : 'text-gray-500'}`}>
                            {shortTeamName(match.away_team)}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0">{match.round}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {h2h && h2h.length === 0 && (
                <div className="text-xs text-gray-400 text-center py-2">No recent head-to-head data</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
